/**
 * Analytics = custom dashboards (screenshots/82–86, brief §2.13). A dashboard is a named layout on the same grid and
 * widgets as the Overview, plus filters, sharing, a print setup and an optional scheduled PDF.
 * Pure model + rules (from the handoff starter's src/analytics/dashboards.ts, on our widget registry); the Worker
 * (worker/src/dashboard-analytics.js) imports this file and enforces the same `canView/canEdit/canShare` checks.
 */
import { can, type FeatureMap, type OrgRole, type UserGroup } from "../nav/navConfig";
import { addItem, paginate, ROWS_PER_PAGE, sanitize, type Layout } from "../overview/grid/layoutEngine";
import { WIDGETS, WIDGET_TYPES, widgetType, type DashboardFilterKey } from "../overview/widgets/registry";
import { DATASETS, type DatasetId } from "../overview/charts/datasets";
import { chartSettings } from "../overview/defaults";
import { allowed } from "../overview/catalog";
import type { WidgetType } from "../overview/types";

export type Principal = { kind: "user"; id: string; name: string; sub?: string } | { kind: "team"; id: string; name: string; sub?: string };
export type Access = "view" | "edit";
export type PrintSetup = { paper: "letter" | "a4"; orientation: "landscape" | "portrait"; colors: "theme" | "grayscale"; appendix: boolean };

export interface Dashboard {
  id: string;
  orgId: string;
  ownerId: string;
  /** Display name of the owner and of the last editor ("edited 2 h ago by Maria"). */
  ownerName?: string;
  updatedByName?: string;
  title: string;
  description?: string;
  /** Private: owner + people/teams in `members`. Team: everyone in the org at `teamAccess`. */
  visibility: "private" | "team";
  teamAccess: Access;
  members: { principal: Principal; access: Access }[];
  link: { mode: "off" | "org" | "public"; token?: string; expiresAt?: string; data: "snapshot" | "live"; publishedAt?: string };
  filters: Partial<Record<DashboardFilterKey, string | string[]>>;
  layout: Layout;
  themeId: string;
  print: PrintSetup;
  schedule?: { weekday: 1 | 2 | 3 | 4 | 5 | 6 | 7; hour: number; to: Principal[]; tz: "America/Chicago" };
  version: number;
  createdAt: string;
  updatedAt: string;
  updatedBy: string;
}

/** features: the server's map for this viewer (featureMap / snapshot:me.features), so staff overrides count. */
export interface Viewer { userId: string; orgId: string; group: UserGroup; role: OrgRole | null; teamIds: string[]; features?: FeatureMap }

// ---------------------------------------------------------------- permissions

export function canView(d: Dashboard, v: Viewer): boolean {
  if (d.orgId !== v.orgId) return false;
  if (!can(v.group, v.role, "dashboards", v.features)) return false;
  // "Anyone in your organization" (85 Link access): everyone in the org can open it (view only, unless added with edit).
  if (d.ownerId === v.userId || d.visibility === "team" || d.link.mode === "org") return true;
  return d.members.some((m) => (m.principal.kind === "user" ? m.principal.id === v.userId : v.teamIds.includes(m.principal.id)));
}

export function canEdit(d: Dashboard, v: Viewer): boolean {
  if (!canView(d, v)) return false;
  if (d.ownerId === v.userId) return true;
  // Org admins edit team dashboards; a private dashboard stays private even from admins.
  if (d.visibility === "team" && (v.role === "owner" || v.role === "admin")) return true;
  if (d.visibility === "team" && d.teamAccess === "edit") return true;
  return d.members.some((m) => m.access === "edit" && (m.principal.kind === "user" ? m.principal.id === v.userId : v.teamIds.includes(m.principal.id)));
}

/** Who can change sharing (and delete): the owner, and org owners/admins on team dashboards. */
export const canShare = (d: Dashboard, v: Viewer) => canView(d, v) && (d.ownerId === v.userId || (d.visibility === "team" && (v.role === "owner" || v.role === "admin")));
/** "Anyone with the link" needs the publicLinks feature (owner/admin role) on top of sharing rights. */
export const canMakePublic = (d: Dashboard, v: Viewer) => canShare(d, v) && can(v.group, v.role, "publicLinks", v.features);

// ---------------------------------------------------------------- links

/** Widgets a link viewer never gets (brief §2.13 privacy note + rule 4): team-only widgets (meeting log, ward responses,
 *  target list, coalitions), the viewer's own feeds (since your last visit, notifications, the team's brief), residents'
 *  widgets (names of people who spoke, how you'd vote), constituents who spoke (private people), and charts on a dataset only some groups may read (Ward responses). */
const LINK_PERSONAL = new Set(["sinceLastVisit", "notifications", "briefMe", "neighborsSpoke", "constituentsSpoke", "howWouldYouVote", "yourAlderperson"]);
export function hiddenOnLinks(item: { type: string; config?: Record<string, unknown> }): boolean {
  const t = widgetType(item.type);
  if (!t) return true;
  if (WIDGETS[t].teamOnly || LINK_PERSONAL.has(t)) return true;
  const ds = (item.config?.settings as { data?: { datasetId?: string } } | undefined)?.data?.datasetId;
  return !!ds && !!DATASETS[ds as DatasetId]?.groups;
}

/** Link viewers: hidden widgets keep their cells but lose their config (same size, so nothing shifts). */
export function redactForLink<T extends Layout[number]>(layout: T[]): T[] {
  return layout.map((i) => (hiddenOnLinks(i) ? { ...i, config: { redacted: true } } : i));
}
export const isLinkValid = (d: Dashboard, token: string, now = new Date()) =>
  d.link.mode === "public" && !!d.link.token && d.link.token === token && (!d.link.expiresAt || new Date(d.link.expiresAt) > now);

/** KV snapshot keys (`dashlink:{token}`) that must be deleted after a share change or delete (`next` = null).
 *  `/d/:token` serves straight from KV, so a reset, a switch away from public, or a delete has to remove the old copy. */
export function staleLinkKeys(prev: Dashboard, next: Dashboard | null): string[] {
  const old = prev.link.token;
  if (!old || prev.link.mode !== "public") return [];
  return !next || next.link.mode !== "public" || next.link.token !== old ? [`dashlink:${old}`] : [];
}

/** 22 random URL-safe characters (≈125 bits, no look-alikes). Rotating the token kills old links. */
export function newLinkToken(rand: (n: number) => Uint8Array = (n) => crypto.getRandomValues(new Uint8Array(n))): string {
  const abc = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789";
  return Array.from(rand(22), (b) => abc[b % abc.length]).join("");
}
export const EXPIRY_DAYS = [7, 30, 90, null] as const;
export const expiryFrom = (days: number | null, now = new Date()) => (days === null ? undefined : new Date(now.getTime() + days * 864e5).toISOString());

// ---------------------------------------------------------------- create

export type TemplateId = "moneyLobbying" | "votesForecasts" | "wardPulse";
export type StartFrom = { kind: "blank" } | { kind: "copyOverview"; layout: Layout } | { kind: "template"; id: TemplateId };

const t = (type: string, x: number, y: number, w: number, h: number, config: Record<string, unknown> = {}) => ({ id: `${type}-${x}-${y}`, type, x, y, w, h, config });
const caa = () => ({ title: "CAA money to alderpersons", meta: "Contributions · since 2019", sources: "all",
  settings: chartSettings("bar", { datasetId: "contributions", mapping: { category: "recipient", value: { field: "amount", agg: "sum" }, tooltip: [] },
    filters: [{ field: "client", op: "is", value: "chicagoland-apartment-association" }], topN: 8, groupRest: false }, "CAA money to alderpersons") });

/** Templates (screenshots/83 Create dashboard). Chart widgets start with settings the user can edit. */
export const TEMPLATES: Record<TemplateId, { title: string; icon: string; groups: UserGroup[]; blurb?: string; layout: ReturnType<typeof t>[] }> = {
  moneyLobbying: { title: "Money & lobbying", icon: "banknote", groups: ["office", "advocacy"],
    blurb: "money by alderperson for a client, filings by topic, lobbying on your items, a coalition map", layout: [
      t("lobbyingOnYourItems", 0, 0, 6, 3), t("oppositionWatch", 6, 0, 6, 3),
      t("chart", 0, 3, 6, 4, caa()),
      t("chart", 6, 3, 6, 4, { title: "Lobbying filings by topic", settings: chartSettings("column", { datasetId: "lobbyingFilings",
        mapping: { category: "topic", value: { field: "filings", agg: "sum" }, tooltip: [] }, filters: [], sort: "valueDesc", topN: 8, groupRest: true }, "Lobbying filings by topic") }),
      t("coalitions", 0, 7, 6, 3),
      t("table", 6, 7, 6, 3, { title: "Top lobbying clients", settings: chartSettings("table", { datasetId: "organizations",
        mapping: { category: "name", value: { field: "items", agg: "sum" }, tooltip: ["filings", "toAlderpersons"] }, filters: [], sort: "valueDesc", topN: 5, groupRest: false }, "Top lobbying clients") }),
    ] },
  votesForecasts: { title: "Votes & forecasts", icon: "sparkles", groups: ["resident", "office", "advocacy"], layout: [
    t("nextVote", 0, 0, 6, 2), t("councilTension", 6, 0, 3, 2), t("forecastWatch", 0, 2, 6, 4),
    t("chart", 6, 2, 6, 4, { title: "Roll calls by vote", settings: chartSettings("column", { datasetId: "rollCalls",
      mapping: { category: "vote", value: { agg: "count" }, tooltip: [] }, filters: [], sort: "valueDesc", groupRest: false }, "Roll calls by vote") }),
    t("upcomingMeetings", 0, 6, 6, 3),
  ] },
  wardPulse: { title: "Ward pulse", icon: "map-pin", groups: ["office"], layout: [
    t("yourWardSaid", 0, 0, 6, 4), t("constituentsWhoSpoke", 6, 0, 3, 2),
    t("chart", 6, 2, 6, 4, { title: "Constituent testimony by topic", settings: chartSettings("column", { datasetId: "testimony",
      mapping: { category: "topic", value: { agg: "count" }, tooltip: [] }, filters: [], sort: "valueDesc", topN: 6, groupRest: true, followViewer: "ward" }, "Constituent testimony by topic") }),
    t("meetingLog", 0, 4, 6, 3),
  ] },
};

/** Widgets this viewer's group (and role) may put on a dashboard; starter names map to ours (ALIASES). */
export function usableBy(raw: Layout, v: Pick<Viewer, "group" | "role" | "features">): Layout {
  return raw.flatMap((i) => {
    const type = widgetType(i.type);
    const d = type && WIDGETS[type];
    if (!d || !(d.audience === "shared" || d.audience === "data" || d.audience === v.group) || (d.feature && !can(v.group, v.role, d.feature, v.features))) return [];
    return [{ ...i, type: type!, config: structuredClone(i.config ?? {}) }];
  });
}

export function createDashboard(input: { title: string; description?: string; start: StartFrom; visibility: "private" | "team"; teamAccess?: Access },
  v: Viewer, ids: { id: string; now?: Date; name?: string }): Dashboard {
  const now = (ids.now ?? new Date()).toISOString();
  const raw: Layout = input.start.kind === "blank" ? [] : input.start.kind === "copyOverview" ? input.start.layout : TEMPLATES[input.start.id].layout;
  return {
    id: ids.id, orgId: v.orgId, ownerId: v.userId, ownerName: ids.name, updatedByName: ids.name,
    title: input.title.trim().slice(0, 80) || "Untitled dashboard", description: input.description?.trim().slice(0, 200) || undefined,
    visibility: input.visibility, teamAccess: input.teamAccess ?? "view", members: [], link: { mode: "off", data: "snapshot" },
    filters: {}, layout: sanitize(usableBy(raw, v), WIDGET_TYPES), themeId: "civic-light",
    print: { paper: "letter", orientation: "landscape", colors: "theme", appendix: true },
    version: 1, createdAt: now, updatedAt: now, updatedBy: v.userId,
  };
}

/** "Add to a dashboard…" from any widget menu. */
export const addWidgetTo = (d: Dashboard, item: { type: string; w: number; h: number; config?: Record<string, unknown> }, id: string): Dashboard =>
  ({ ...d, layout: addItem(d.layout, { id, type: item.type, w: item.w, h: item.h, config: item.config ?? {} }) });

// ---------------------------------------------------------------- list (screenshots/82)

export type ListFilter = "all" | "starred" | "yours" | "team" | "shared";
export function listDashboards(all: Dashboard[], v: Viewer, starred: string[], f: ListFilter, q = "", sort: "edited" | "title" = "edited") {
  const visible = all.filter((d) => canView(d, v));
  const shared = (d: Dashboard) => d.ownerId !== v.userId && d.visibility === "private";
  const counts = {
    all: visible.length, starred: visible.filter((d) => starred.includes(d.id)).length, yours: visible.filter((d) => d.ownerId === v.userId).length,
    team: visible.filter((d) => d.visibility === "team").length, shared: visible.filter(shared).length,
  };
  const list = visible.filter((d) => f === "all" || (f === "starred" && starred.includes(d.id)) || (f === "yours" && d.ownerId === v.userId)
    || (f === "team" && d.visibility === "team") || (f === "shared" && shared(d)))
    .filter((d) => d.title.toLowerCase().includes(q.trim().toLowerCase()))
    .sort((a, b) => (sort === "title" ? a.title.localeCompare(b.title) : b.updatedAt.localeCompare(a.updatedAt)));
  return { list, counts, starred: list.filter((d) => starred.includes(d.id)).sort((a, b) => starred.indexOf(a.id) - starred.indexOf(b.id)) };
}

export const accessLabel = (d: Dashboard) => (d.link.mode === "public" ? "Link" : d.visibility === "team" ? "Team" : d.members.length ? "Shared" : "Only you");

/** "edited 2 h ago by Alex" · "edited yesterday by Maria" · "edited Oct 3 by you" (82 card meta). */
export function editedLine(d: Dashboard, me: string, now = new Date()): string {
  const at = new Date(d.updatedAt), mins = Math.round((now.getTime() - at.getTime()) / 6e4);
  const day = (x: Date) => x.toLocaleDateString("en-CA", { timeZone: "America/Chicago" });
  const yesterday = day(new Date(now.getTime() - 864e5)) === day(at);
  const when = mins < 60 ? `${Math.max(1, mins)} min ago` : mins < 24 * 60 && !yesterday ? `${Math.round(mins / 60)} h ago` : yesterday ? "yesterday"
    : at.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "America/Chicago" });
  const by = d.updatedBy === me ? "you" : d.updatedByName ?? "your team";
  return `edited ${when} by ${by}`;
}

// ---------------------------------------------------------------- print (screenshots/86)

export interface PrintPlan { pages: Layout[]; header: { title: string; filters: string; asOf: string; link?: string }; footer: string; appendix: boolean }

export function printPlan(d: Dashboard, o: { asOf: string; filtersText: string; origin: string }): PrintPlan {
  const rows = ROWS_PER_PAGE[d.print.paper][d.print.orientation];
  return {
    pages: paginate(d.layout, rows),   // live widgets print their current value
    header: { title: d.title, filters: o.filtersText, asOf: `Data as of ${o.asOf}`, link: d.link.mode !== "off" && d.link.token ? `${o.origin}/d/${d.link.token}` : undefined },
    footer: `Money near a vote isn’t evidence of influence. Sources: ISBE, Board of Ethics, City Clerk.`,
    appendix: d.print.appendix,
  };
}

/** Next scheduled PDF in Chicago time (the Worker's hourly cron sends what's due). */
export function nextRun(s: NonNullable<Dashboard["schedule"]>, now: Date): Date {
  const chi = new Date(now.toLocaleString("en-US", { timeZone: s.tz }));
  const offset = now.getTime() - chi.getTime();
  const target = new Date(chi);
  target.setHours(s.hour, 0, 0, 0);
  const isoDay = ((chi.getDay() + 6) % 7) + 1;
  let add = (s.weekday - isoDay + 7) % 7;
  if (add === 0 && target <= chi) add = 7;
  target.setDate(target.getDate() + add);
  return new Date(target.getTime() + offset);
}
/** Is a scheduled PDF due in this hourly cron run? (`lastSent` = when we last sent it.) */
export const pdfDue = (s: NonNullable<Dashboard["schedule"]>, now: Date, lastSent: string | null) => {
  const prev = nextRun(s, new Date(now.getTime() - 7 * 864e5));   // the run in the past 7 days
  return prev <= now && (!lastSent || new Date(lastSent) < prev);
};

/** Three first widgets for an empty dashboard (screenshots/116): the first ones from this group's templates. */
export function suggestionsFor(group: UserGroup, role: OrgRole | null = null): { type: WidgetType; title: string; icon: string }[] {
  const seen = new Set<string>(), out: { type: WidgetType; title: string; icon: string }[] = [];
  for (const tpl of Object.values(TEMPLATES)) {
    if (!tpl.groups.includes(group)) continue;
    for (const i of tpl.layout) {
      const w = WIDGETS[i.type as WidgetType];
      if (!w || seen.has(i.type) || w.audience === "data" || !allowed(w, group, role)) continue;
      seen.add(i.type); out.push({ type: w.type, title: w.title, icon: w.icon });
      if (out.length === 3) return out;
    }
  }
  return out;
}
